"use client";
import { httpBatchLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { useState } from "react";
import superjson from "superjson";
import { getQueryClient } from "@/lib/query-client";
import type { AppRouter } from "@/server/api/types";

// Removed deduplication - was causing issues with infinite queries and complex tRPC operations

function getBaseUrl() {
	if (typeof window !== "undefined") {
		// Browser should use relative path
		return "";
	}

	if (process.env.VERCEL_URL) {
		// Reference for vercel.com
		return `https://${process.env.VERCEL_URL}`;
	}

	if (process.env.NEXT_PUBLIC_APP_URL) {
		// Use app URL if set
		return process.env.NEXT_PUBLIC_APP_URL;
	}

	// Assume localhost
	return "http://localhost:3000";
}

export const trpc: ReturnType<typeof createTRPCReact<AppRouter>> =
	createTRPCReact<AppRouter>();

interface TRPCReactProviderProps {
	children: React.ReactNode;
}

export function TRPCReactProvider({ children }: TRPCReactProviderProps) {
	const queryClient = getQueryClient();

	const [trpcClient] = useState(() =>
		trpc.createClient({
			links: [
				httpBatchLink({
					url: `${getBaseUrl()}/api/trpc`,
					transformer: superjson,
					// Enhanced batching configuration (tRPC v11 supports automatic batching)
					maxURLLength: 2083, // Optimize URL length for batching
					headers() {
						return {
							"x-trpc-source": "react",
							"x-trpc-version": "v5", // Track client version
						};
					},
					// Enhanced error handling with proper retry logic
					fetch(url, options) {
						return fetch(url, {
							...options,
							// CRITICAL: Include credentials for Supabase auth cookies
							// Without this, authenticated tRPC procedures will fail
							credentials: "include",
							// Better timeout handling
							signal: AbortSignal.timeout(30000), // 30 second timeout
						} as RequestInit).catch((error) => {
							// Handle AbortError gracefully
							if (error.name === 'AbortError') {
								console.warn('Request timeout after 30 seconds:', url);
								// Re-throw with a more user-friendly error
								throw new Error('Request timeout. Please try again.');
							}
							// Re-throw other errors unchanged
							throw error;
						});
					},
				}),
			],
		}),
	);

	return (
		<trpc.Provider
			client={trpcClient}
			queryClient={
				// Type assertion needed due to version mismatch between @tanstack/react-query
				// and @trpc/react-query internal query-core dependencies
				// Using unknown as intermediate type for safer assertion
				queryClient as unknown as Parameters<typeof trpc.Provider>[0]['queryClient']
			}
		>
			{children}
		</trpc.Provider>
	);
}
